import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllUserAddresses, getTransferRequests } from '../services/auditorService';
import { assetUrl } from '../utils/assets';
import { toUserFriendlyError } from '../utils/friendlyError';
import UserSelector from './UserSelector';
import TransactionList from './TransactionList';
import AccessFilter from './AccessFilter';
import ChangePasswordModal from './ChangePasswordModal';
import './Dashboard.css';

const EMPTY_ACCESS = [];

const Dashboard = ({ user, auditorData, onLogout, isAdmin = false, onKeyMaterialUpdated }) => {
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState(null);
  const [userAddresses, setUserAddresses] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [filters, setFilters] = useState({
    chain: null,
    token: null,
    application: null,
    userAddress: null,
  });
  const usersRequestIdRef = useRef(0);
  const transactionsRequestIdRef = useRef(0);

  const accessControls = useMemo(
    () => auditorData?.accessControls || auditorData?.access_controls || EMPTY_ACCESS,
    [auditorData?.accessControls, auditorData?.access_controls]
  );

  useEffect(() => {
    document.title = 'Compliance Dashboard';
  }, []);

  const loadUserAddresses = useCallback(async () => {
    const requestId = ++usersRequestIdRef.current;
    try {
      setUsersLoading(true);
      setError(null);
      const addresses = await getAllUserAddresses(accessControls);
      if (requestId !== usersRequestIdRef.current) return;
      setUserAddresses(addresses);
    } catch (err) {
      if (requestId !== usersRequestIdRef.current) return;
      setError(toUserFriendlyError(err, 'Failed to load user accounts. Please try again.'));
      console.error('Error loading user addresses:', err);
    } finally {
      if (requestId === usersRequestIdRef.current) {
        setUsersLoading(false);
      }
    }
  }, [accessControls]);

  const loadTransactions = useCallback(async () => {
    const requestId = ++transactionsRequestIdRef.current;
    try {
      setTransactionsLoading(true);
      setError(null);
      const requestFilters = {
        ...filters,
        userAddress: filters.userAddress || selectedUser || null,
      };
      const auditorGrantIds = [
        auditorData?.grantId,
        auditorData?.uid,
        auditorData?.id,
      ].filter((id) => typeof id === 'string' && id.length > 0);
      const txs = await getTransferRequests(requestFilters, accessControls, auditorGrantIds);
      if (requestId !== transactionsRequestIdRef.current) return;
      setTransactions(txs);
    } catch (err) {
      if (requestId !== transactionsRequestIdRef.current) return;
      setError(toUserFriendlyError(err, 'Failed to load transfers. Please try again.'));
      console.error('Error loading transactions:', err);
    } finally {
      if (requestId === transactionsRequestIdRef.current) {
        setTransactionsLoading(false);
      }
    }
  }, [filters, selectedUser, accessControls, auditorData?.grantId, auditorData?.uid, auditorData?.id]);

  useEffect(() => {
    loadUserAddresses();
  }, [loadUserAddresses]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters((prev) => {
      const next = {
        chain: newFilters.chain ?? null,
        token: newFilters.token ?? null,
        application: newFilters.application || null,
        // Preserve selected account unless AccessFilter explicitly keeps a userAddress value.
        // Clear All only clears chain/token/application and must not wipe the selected user.
        userAddress:
          Object.prototype.hasOwnProperty.call(newFilters, 'userAddress')
            ? newFilters.userAddress
            : prev.userAddress,
      };

      const unchanged =
        prev.chain === next.chain &&
        prev.token === next.token &&
        prev.application === next.application &&
        prev.userAddress === next.userAddress;
      return unchanged ? prev : next;
    });

    if (
      Object.prototype.hasOwnProperty.call(newFilters, 'userAddress') &&
      !newFilters.userAddress
    ) {
      setSelectedUser(null);
    }
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      chain: null,
      token: null,
      application: null,
    }));
  }, []);

  const handleUserSelect = useCallback((userAddress) => {
    const effectiveSelected = selectedUser || filters.userAddress;
    const isAlreadySelected =
      effectiveSelected &&
      effectiveSelected.toLowerCase() === userAddress.toLowerCase();
    if (isAlreadySelected) {
      setSelectedUser(null);
      setFilters((prev) => ({ ...prev, userAddress: null }));
    } else {
      setSelectedUser(userAddress);
      setFilters((prev) => ({
        ...prev,
        userAddress,
        chain: null,
        token: null,
        application: null,
      }));
    }
  }, [selectedUser, filters.userAddress]);

  const handleClearUser = useCallback(() => {
    setSelectedUser(null);
    setFilters((prev) => ({ ...prev, userAddress: null }));
  }, []);

  const handleRemoveFilter = useCallback((key) => {
    if (key === 'userAddress') {
      handleClearUser();
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: null }));
  }, [handleClearUser]);

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-title">
            <img
              src={assetUrl('logo.png')}
              alt="Compliance Dashboard logo"
              className="dashboard-logo"
            />
            <div className="header-title-text">
              <h1>Compliance Dashboard</h1>
              <span className="header-subtitle">Confidential transfer auditing</span>
            </div>
          </div>
          <div className="header-actions">
            {isAdmin && (
              <button
                type="button"
                onClick={() => navigate('/admin/requests')}
                className="header-nav-button"
              >
                Signup Requests
              </button>
            )}
            <div className="header-user">
              <span className="header-user-avatar" aria-hidden="true">
                {(user.email || '?').charAt(0)}
              </span>
              <span className="user-email">{user.email}</span>
            </div>
            <button
              type="button"
              onClick={() => setChangePasswordOpen(true)}
              className="header-nav-button"
            >
              Change Password
            </button>
            <button type="button" onClick={onLogout} className="logout-button">
              Log Out
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <aside className="dashboard-sidebar">
          <AccessFilter
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            auditorAccessControls={accessControls}
            userAddress={filters.userAddress ?? ''}
            selectedChain={filters.chain || ''}
            selectedToken={filters.token || ''}
            selectedApplication={filters.application || ''}
            transactions={transactions}
            loading={transactionsLoading && transactions.length === 0}
          />
          <UserSelector
            users={userAddresses}
            selectedUser={selectedUser || filters.userAddress}
            onSelectUser={handleUserSelect}
            onClearUser={handleClearUser}
            loading={usersLoading}
          />
        </aside>

        <div className="dashboard-content">
          {error && (
            <div className="error-banner ui-alert ui-alert--error" role="alert">
              <span>{error}</span>
              <button
                type="button"
                className="ui-btn ui-btn--ghost"
                onClick={() => setError(null)}
                aria-label="Dismiss error"
              >
                Dismiss
              </button>
            </div>
          )}

          <TransactionList
            userAddress={filters.userAddress || selectedUser}
            transactions={transactions}
            loading={transactionsLoading}
            auditorData={auditorData}
            filters={filters}
            onRemoveFilter={handleRemoveFilter}
          />
        </div>
      </main>

      {changePasswordOpen && (
        <ChangePasswordModal
          user={user}
          auditorDocId={auditorData.id}
          keyMaterial={{
            publicKey: auditorData.publicKey,
            encPrivKey: auditorData.encPrivKey,
            salt: auditorData.salt,
            kdfParams: auditorData.kdfParams,
            iv: auditorData.iv,
            keyVersion: auditorData.keyVersion,
          }}
          onClose={() => setChangePasswordOpen(false)}
          onSuccess={(updated) => {
            onKeyMaterialUpdated?.(updated);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
